import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Icon } from '@iconify/react';
import SEO from '../../../Components/SEO';
import useCart from '../hooks/useCart';
import CITIES from '../data/cities.json';

import flexpayService, { PAYMENT_METHODS } from './components/flexpayService';
import StepIndicator from './components/StepIndicator';
import StepLivraison from './components/StepLivraison';
import StepPaiement from './components/StepPaiement';
import StepConfirmation from './components/StepConfirmation';
import OrderSummary from './components/OrderSummary';

//Templates
import ReceiptTemplate from './components/ReceiptTemplate';

const Checkout = () => {
    const navigate = useNavigate();
    const { cart, cartCount } = useCart();

    // Steps: 0 = livraison, 1 = paiement, 2 = confirmation
    const [step, setStep] = useState(0);
    const [finalOrderPayload, setFinalOrderPayload] = useState(null);
    const [selectedCity, setSelectedCity] = useState('lubumbashi');
    const [selectedDelivery, setSelectedDelivery] = useState(null);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [customerInfo, setCustomerInfo] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
    });

    // Payment form fields
    const [paymentPhone, setPaymentPhone] = useState('');
    const [cardInfo, setCardInfo] = useState({
        number: '',
        expiry: '',
        cvv: '',
        holderName: '',
    });

    // Transaction state
    const [txState, setTxState] = useState('idle');
    const [txMessage, setTxMessage] = useState('');
    const [txOrderNumber, setTxOrderNumber] = useState('');
    const [txReference, setTxReference] = useState('');
    const pollingRef = useRef(null);

    // Computed values
    const subtotal = cart.reduce((sum, item) => sum + item.price * item.qty, 0);
    const cityData = useMemo(() => CITIES.find((c) => c.id === selectedCity), [selectedCity]);
    const deliveryData = useMemo(
        () => cityData?.deliveryModes.find((d) => d.id === selectedDelivery),
        [cityData, selectedDelivery]
    );
    const shippingCost = deliveryData?.price || 0;
    const total = subtotal + shippingCost;

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, []);

    // Scroll to top on step change
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [step]);

    // Prepare and send payload on success
    const payloadSentRef = useRef(false);
    useEffect(() => {
        if (txState === 'success' && !payloadSentRef.current) {
            payloadSentRef.current = true;

            const orderPayload = {
                customer: {
                    name: customerInfo.name,
                    email: customerInfo.email,
                    phone: customerInfo.phone,
                    address: customerInfo.address,
                },
                delivery: {
                    city: cityData?.name,
                    mode: deliveryData?.label,
                    price: shippingCost,
                    delay: deliveryData?.delay,
                },
                payment: {
                    method: selectedPayment,
                    phone: paymentPhone,
                    reference: txReference,
                    orderNumber: txOrderNumber,
                },
                items: cart.map(item => ({
                    id: item.id,
                    name: item.name,
                    price: item.price,
                    qty: item.qty,
                    total: item.price * item.qty
                })),
                summary: {
                    subtotal,
                    shippingCost,
                    total
                },
                status: 'paid',
                createdAt: new Date().toISOString()
            };

            // console.log('Payload prêt pour Strapi :', orderPayload);
            setFinalOrderPayload(orderPayload);
        }
    }, [
        txState, customerInfo, cityData, deliveryData, shippingCost,
        selectedPayment, paymentPhone, txReference, txOrderNumber,
        cart, subtotal, total
    ]);

    // Générer l'image du reçu une fois le payload prêt
    useEffect(() => {
        if (finalOrderPayload) {
            const orderRef = finalOrderPayload.payment?.reference;
            if (!orderRef) return;

            // Vérifier si cette commande a déjà été traitée localement
            const isProcessed = localStorage.getItem(`sd_order_${orderRef}`);
            if (isProcessed) {
                console.log('ℹ️ Commande déjà traitée, envoi annulé pour éviter les doublons.');
                return;
            }

            // Marquer comme traitée immédiatement
            localStorage.setItem(`sd_order_${orderRef}`, 'true');

            // Laisse un léger délai à React pour bien peindre le composant caché
            setTimeout(() => {
                import('html2canvas').then(async ({ default: html2canvas }) => {
                    const elt = document.getElementById('receipt-template');
                    if (elt) {
                        try {
                            const canvas = await html2canvas(elt, {
                                scale: 2, // Haute qualité
                                useCORS: true,
                                backgroundColor: '#f8fafc'
                            });

                            canvas.toBlob(blob => {
                                // Import dynamique du service d'envoi d'email pour l'image attachée
                                import('./components/mailerService').then(module => {
                                    module.default.sendOrderEmail(finalOrderPayload, blob);
                                }).catch(console.error);
                            }, 'image/jpeg', 0.95);
                        } catch (err) {
                            console.error("Erreur lors de la création du reçu image :", err);
                        }
                    }
                });
            }, 600);
        }
    }, [finalOrderPayload]);

    const generateReference = () =>
        `SD-${Date.now()}-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;

    // Poll transaction status
    const pollTransaction = useCallback((orderNumber) => {
        setTxState('polling');
        setTxMessage('Vérification du paiement en cours...');
        let attempts = 0;
        const maxAttempts = 30;

        pollingRef.current = setInterval(async () => {
            attempts++;
            try {
                const result = await flexpayService.checkTransaction(orderNumber);
                if (result?.transaction) {
                    const status = String(result.transaction.status);
                    if (status === '0') {
                        clearInterval(pollingRef.current);
                        setTxState('success');
                        setTxMessage('Paiement effectué avec succès !');
                    } else if (status === '1') {
                        setTxMessage(`En attente de confirmation... (${attempts}/${maxAttempts})`);
                    } else if (status === '2') {
                        clearInterval(pollingRef.current);
                        setTxState('failed');
                        setTxMessage(result.transaction.message || 'La transaction a échoué.');
                    }
                }
            } catch (err) {
                console.error('Polling error:', err);
            }
            if (attempts >= maxAttempts) {
                clearInterval(pollingRef.current);
                setTxState('failed');
                setTxMessage('Délai d\'attente dépassé. Veuillez vérifier votre compte ou réessayer.');
            }
        }, 5000);
    }, []);

    // Initiate payment
    const handlePayment = async () => {
        const paymentMethod = PAYMENT_METHODS.find((m) => m.id === selectedPayment);
        if (!paymentMethod) return;

        const reference = generateReference();
        setTxReference(reference);
        setTxState('processing');
        setTxMessage('Envoi de la demande de paiement...');
        setStep(2);

        try {
            const result = await flexpayService.initiatePayment({
                amount: Math.round(total * 100) / 100,
                currency: 'USD',
                phone: paymentPhone.replace(/\s+/g, ''),
                reference,
                transactionType: paymentMethod.transactionType,
            });

            if (result?.code === '0' && result?.orderNumber) {
                setTxOrderNumber(result.orderNumber);
                pollTransaction(result.orderNumber);
            } else {
                setTxState('failed');
                setTxMessage(result?.message || 'Échec d\'initialisation du paiement. Vérifiez vos informations.');
            }
        } catch (error) {
            console.error('Payment error:', error);
            setTxState('failed');
            setTxMessage('Erreur de connexion. Vérifiez votre réseau et réessayez.');
        }
    };

    // Empty cart guard
    if (cart.length === 0 && txState !== 'success') {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-store-black flex flex-col items-center justify-center gap-4 text-center px-4 transition-colors">
                <div className="text-6xl">🛒</div>
                <h1 className="text-slate-900 dark:text-white text-2xl font-bold">
                    Votre panier est vide
                </h1>
                <p className="text-slate-500 dark:text-store-gray">
                    Ajoutez des produits avant de passer au checkout.
                </p>
                <Link
                    to="/Boutique"
                    className="mt-4 bg-lime-500 text-black font-bold px-6 py-3 rounded-xl hover:bg-lime-400 transition-colors shadow-lg shadow-lime-500/20"
                >
                    <Icon icon="guidance:left-arrow" className='text-xl group-hover:translate-x-2 transition-all duration-300' />
                    Retour à la boutique
                </Link>
            </div>
        );
    }

    // Validation flags
    const canProceedStep0 =
        selectedCity && selectedDelivery && customerInfo.name && customerInfo.phone &&
        (selectedDelivery !== 'domicile' || customerInfo.address);

    const canProceedStep1 =
        selectedPayment &&
        (selectedPayment === 'mobile-money'
            ? paymentPhone.trim().length >= 10
            : cardInfo.number && cardInfo.expiry && cardInfo.cvv && cardInfo.holderName);

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-store-black text-slate-900 dark:text-white transition-colors duration-300 mt-12">
            <SEO
                title="Checkout — Safaridew Store"
                description="Finalisez votre commande sur Safaridew Store."
                canonical="https://safaridew.vercel.app/Boutique/checkout"
            />

            {/* Top nav */}
            <div className="bg-white/80 dark:bg-store-dark/80 backdrop-blur border-b border-slate-200 dark:border-store-border px-4 py-3">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-store-gray">
                        <Link
                            to="/Boutique"
                            className="hover:text-lime-600 dark:hover:text-lime-400 transition-colors"
                        >
                            Boutique
                        </Link>
                        <Icon icon="guidance:left-arrow" className="text-[10px]" />
                        <span className="text-slate-900 dark:text-white font-medium">
                            Checkout
                        </span>
                    </div>

                    <button
                        onClick={() => navigate('/Boutique')}
                        className="text-slate-500 dark:text-store-gray hover:text-slate-900 dark:hover:text-white transition-colors text-xs flex items-center gap-1"
                    >
                        <Icon icon="guidance:right-arrow" />
                        Retour
                    </button>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 py-8">
                {txState !== 'success' && (
                    <StepIndicator
                        current={step}
                        steps={['Livraison', 'Paiement', 'Confirmation']}
                    />
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main content */}
                    <div className="lg:col-span-2">
                        <AnimatePresence mode="wait">
                            {step === 0 && (
                                <StepLivraison
                                    customerInfo={customerInfo}
                                    setCustomerInfo={setCustomerInfo}
                                    selectedCity={selectedCity}
                                    setSelectedCity={setSelectedCity}
                                    selectedDelivery={selectedDelivery}
                                    setSelectedDelivery={setSelectedDelivery}
                                    cityData={cityData}
                                    canProceed={canProceedStep0}
                                    onContinue={() => setStep(1)}
                                />
                            )}

                            {step === 1 && (
                                <StepPaiement
                                    customerInfo={customerInfo}
                                    cityData={cityData}
                                    deliveryData={deliveryData}
                                    selectedPayment={selectedPayment}
                                    setSelectedPayment={setSelectedPayment}
                                    paymentPhone={paymentPhone}
                                    setPaymentPhone={setPaymentPhone}
                                    cardInfo={cardInfo}
                                    setCardInfo={setCardInfo}
                                    canProceed={canProceedStep1}
                                    total={total}
                                    onBack={() => setStep(0)}
                                    onPay={handlePayment}
                                />
                            )}

                            {step === 2 && (
                                <StepConfirmation
                                    txState={txState}
                                    txMessage={txMessage}
                                    txReference={txReference}
                                    txOrderNumber={txOrderNumber}
                                    selectedPayment={selectedPayment}
                                    paymentPhone={paymentPhone}
                                    customerInfo={customerInfo}
                                    cityData={cityData}
                                    deliveryData={deliveryData}
                                    total={total}
                                    onRetry={() => {
                                        setTxState('idle');
                                        setStep(1);
                                    }}
                                />
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Order summary sidebar */}
                    {txState !== 'success' && (
                        <OrderSummary
                            cart={cart}
                            cartCount={cartCount}
                            subtotal={subtotal}
                            shippingCost={shippingCost}
                            total={total}
                            deliveryData={deliveryData}
                        />
                    )}
                </div>
            </div>

            {/* Template caché du Reçu pour la capture html2canvas */}
            <ReceiptTemplate payload={finalOrderPayload} />

        </div>
    );
};

export default Checkout;
